// Seating plans -- where a class sits, room by room.
//
// A plan is a GRID OF CELLS, not a list of seats. That is the difference that makes real rooms
// work: a room is rows by columns with things missing -- an aisle, a pillar, the teacher's desk --
// and a plain list of seats cannot say where the holes are. Every cell is one of three things, and
// the school says which by clicking it.
//
// THE PLAN OWNS THE SEATING, NOT THE STUDENT. A student is written into a cell; nothing is written
// back onto the student. So a plan can be built, thrown away, or kept alongside three others for
// the same class without any of them contradicting the roll.

export const CELL = {
  SEAT: 'seat',       // a student can sit here
  TEACHER: 'teacher', // the teacher's desk
  BLOCKED: 'blocked', // an aisle, a pillar, a room that is not a rectangle
};

/** Cells are keyed "row-col" so a plain object can hold a sparse grid. */
export const cellKey = (row, col) => `${row}-${col}`;

export const parseCellKey = (key) => {
  const [r, c] = String(key || '').split('-').map(Number);
  return { row: r, col: c };
};

/** What a cell is. Anything unmarked is an ordinary seat, so a new grid is all seats. */
export const cellTypeAt = (plan, row, col) => (plan?.cells?.[cellKey(row, col)] || CELL.SEAT);

/** Clicking a cell walks it round the three states, which is the whole of the setup interaction. */
export const nextCellType = (type) => (
  type === CELL.SEAT ? CELL.TEACHER : type === CELL.TEACHER ? CELL.BLOCKED : CELL.SEAT
);

/**
 * Every cell of the grid, in reading order, with what it is and who is in it.
 *
 * Built fresh each render rather than stored: the stored plan holds only what the school decided
 * (the size, the cells that are not ordinary seats, who sits where), and everything else follows.
 */
export const buildGrid = (plan) => {
  const rows = Math.max(0, Number(plan?.rows) || 0);
  const cols = Math.max(0, Number(plan?.cols) || 0);
  const out = [];
  for (let r = 0; r < rows; r += 1) {
    const line = [];
    for (let c = 0; c < cols; c += 1) {
      const key = cellKey(r, c);
      const type = cellTypeAt(plan, r, c);
      line.push({ key, row: r, col: c, type, student: type === CELL.SEAT ? (plan?.seats?.[key] || null) : null });
    }
    out.push(line);
  }
  return out;
};

/** Keys of every cell a student may sit in. */
export const seatKeys = (plan) => (
  buildGrid(plan).flat().filter(c => c.type === CELL.SEAT).map(c => c.key)
);

/** Seats with nobody in them. */
export const emptySeatKeys = (plan) => seatKeys(plan).filter(k => !plan?.seats?.[k]);

/**
 * Everybody sitting in a cell that is ACTUALLY A SEAT.
 *
 * The qualifier is the whole point. `plan.seats` is a plain map and nothing stops it holding an
 * entry for a cell that has since become the teacher's desk, been blocked out, or fallen outside a
 * shrunken room. Such an entry is a ghost: the student is drawn nowhere, yet a naive read of the
 * map counts them as seated -- so they vanish from the "not seated" list as well, and the plan is
 * saved with them nowhere at all. That is exactly how five students disappeared.
 *
 * Deriving this from the grid instead of from the map makes the ghost impossible to believe in.
 */
export const seatedEmails = (plan) => {
  const set = new Set();
  seatKeys(plan).forEach(k => {
    const e = String(plan?.seats?.[k]?.email || '').toLowerCase();
    if (e) set.add(e);
  });
  return set;
};

/**
 * Students on the roster who are not sitting anywhere yet.
 *
 * Read off the plan's own grid rather than a flag on the student, so a student removed from a seat
 * is instantly back in this list with nothing to keep in step.
 */
export const unseatedStudents = (students, plan) => {
  const seated = seatedEmails(plan);
  return (students || []).filter(s => !seated.has(String(s.email).toLowerCase()));
};

/**
 * Drop the ghosts: seat entries whose cell is no longer a seat.
 *
 * Run when a plan is opened and again before it is saved, so a plan that already carries ghosts
 * from an earlier version heals itself the first time somebody looks at it. The students come back
 * as unseated, which is visible and true, rather than silently missing.
 */
export const pruneSeats = (plan) => {
  const valid = new Set(seatKeys(plan));
  const seats = {};
  let dropped = 0;
  Object.entries(plan?.seats || {}).forEach(([key, v]) => {
    if (valid.has(key)) seats[key] = v; else dropped += 1;
  });
  return { seats, dropped };
};

/**
 * Seats holding somebody who is no longer on the roster.
 *
 * Happens for real: a student leaves the class, or a group is edited after the plan was built.
 * Surfaced rather than cleaned up automatically -- silently emptying a seat somebody arranged is
 * how a plan stops matching the room without anyone noticing.
 */
export const strandedSeats = (students, plan) => {
  const roster = new Set((students || []).map(s => String(s.email).toLowerCase()));
  // Only real seats: a ghost entry is a different fault with a different fix (see pruneSeats), and
  // reporting it here would send the school looking for a student who never left.
  return seatKeys(plan)
    .filter(key => {
      const e = String(plan?.seats?.[key]?.email || '').toLowerCase();
      return e && !roster.has(e);
    })
    .map(key => ({ key, student: plan.seats[key] }));
};

/** Put a student in a seat, taking them out of wherever they were sitting before. */
export const seatStudent = (seats, key, student) => {
  const next = { ...(seats || {}) };
  const email = String(student?.email || '').toLowerCase();
  Object.keys(next).forEach(k => {
    if (String(next[k]?.email || '').toLowerCase() === email) delete next[k];
  });
  next[key] = student;
  return next;
};

/** Empty one seat. */
export const clearSeat = (seats, key) => {
  const next = { ...(seats || {}) };
  delete next[key];
  return next;
};

/** Two students trade places -- what dragging one onto an occupied seat should mean. */
export const swapSeats = (seats, a, b) => {
  if (a === b) return { ...(seats || {}) };
  const next = { ...(seats || {}) };
  const av = next[a];
  const bv = next[b];
  if (bv) next[a] = bv; else delete next[a];
  if (av) next[b] = av; else delete next[b];
  return next;
};

/**
 * Fill the empty seats with the students who are not sitting yet.
 *
 * ONLY the empty seats and ONLY the unseated students: somebody who has already been placed by hand
 * stays exactly where they were put. A shuffle that threw away the arrangement you had just made
 * would be a button nobody dares press twice.
 *
 * `rand` is injectable so the shuffle can be tested; the app passes nothing and gets Math.random.
 */
export const autoAssign = (plan, students, rand = Math.random) => {
  const pool = [...unseatedStudents(students, plan)];
  // Fisher-Yates, so every arrangement is equally likely -- sort(() => rand() - 0.5) is not a
  // shuffle and biases badly on real engines.
  for (let i = pool.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  const open = emptySeatKeys(plan);
  const next = { ...(plan?.seats || {}) };
  open.forEach((key, i) => {
    if (i < pool.length) {
      const s = pool[i];
      next[key] = {
        email: s.email, sid: s.sid || '', name: s.name || '',
        currentClass: s.currentClass || '', classNumber: s.classNumber || '',
      };
    }
  });
  return { seats: next, placed: Math.min(open.length, pool.length), leftOver: Math.max(0, pool.length - open.length) };
};

/**
 * Resize the room, reporting what falls off the edge.
 *
 * Shrinking a grid throws away whatever was in the cells that no longer exist. The count comes back
 * with the new plan so the screen can say "3 students will lose their seat" BEFORE it happens,
 * rather than the school discovering it afterwards.
 */
export const resizeGrid = (plan, rows, cols) => {
  const r = Math.max(1, Math.round(Number(rows) || 0));
  const c = Math.max(1, Math.round(Number(cols) || 0));
  const inside = (key) => {
    const { row, col } = parseCellKey(key);
    return row < r && col < c;
  };
  const seats = {};
  let displaced = 0;
  Object.entries(plan?.seats || {}).forEach(([key, v]) => {
    if (inside(key)) seats[key] = v; else displaced += 1;
  });
  const cells = {};
  Object.entries(plan?.cells || {}).forEach(([key, v]) => { if (inside(key)) cells[key] = v; });
  // A gap after the last column is not a gap between anything, so it goes with the column it followed.
  const colGaps = {};
  Object.keys(plan?.colGaps || {}).forEach(k => { if (Number(k) < c - 1) colGaps[k] = true; });
  const rowGaps = {};
  Object.keys(plan?.rowGaps || {}).forEach(k => { if (Number(k) < r - 1) rowGaps[k] = true; });
  return { rows: r, cols: c, seats, cells, colGaps, rowGaps, displaced };
};

// ── Aisles ─────────────────────────────────────────────────────────────────────────────────────
//
// A classroom is not evenly spaced. Two desks are pushed together, then there is a walkway, then
// two more. An aisle is therefore BETWEEN columns, not a column of its own -- blocking out a whole
// cell to stand for it would claim a desk's worth of floor for a gap you can barely stand in, and
// would make a 30-seat room need 40 cells to describe.
//
// Kept as its own fact for the same reason the door is: it is a property of the room, and it does
// not touch which cells are seats. Adding an aisle to an arranged plan moves nobody.

/** Gaps are keyed by the index of the column they follow, so gap 1 sits between columns 1 and 2. */
export const hasColGap = (plan, col) => !!plan?.colGaps?.[String(col)];
export const hasRowGap = (plan, row) => !!plan?.rowGaps?.[String(row)];

const toggleGap = (map, index) => {
  const next = { ...(map || {}) };
  const key = String(index);
  if (next[key]) delete next[key]; else next[key] = true;
  return next;
};

export const toggleColGap = (plan, col) => toggleGap(plan?.colGaps, col);
export const toggleRowGap = (plan, row) => toggleGap(plan?.rowGaps, row);

/**
 * The common Hong Kong layout in one press: desks in pairs, an aisle after every second column.
 *
 * Offered rather than made the default. A room laid out in threes, or in one long row, is a real
 * room too, and starting every plan with aisles somebody has to delete is worse than starting flat.
 */
export const pairColumns = (cols) => {
  const gaps = {};
  for (let c = 2; c < Number(cols || 0); c += 2) gaps[String(c - 1)] = true;
  return gaps;
};

/** No aisles at all -- what the button toggles back to. */
export const noGaps = () => ({});

/**
 * A door sits on a wall, not in a cell -- `{ edge, index }` where index counts along that wall.
 *
 * Kept off the grid on purpose: a door is not somewhere a person or a desk can be, and putting it
 * in a cell would mean the room silently loses a seat to it.
 */
export const doorSlots = (plan, edge) => (
  edge === 'top' || edge === 'bottom' ? Math.max(0, Number(plan?.cols) || 0) : Math.max(0, Number(plan?.rows) || 0)
);

export const isDoorAt = (plan, edge, index) => (
  plan?.door?.edge === edge && Number(plan?.door?.index) === index
);

/** How full the plan is, for the line that tells the school whether it is finished. */
export const planSummary = (plan, students) => {
  const seats = seatKeys(plan).length;
  const filled = seatKeys(plan).filter(k => plan?.seats?.[k]).length;
  const roster = (students || []).length;
  return {
    seats,
    filled,
    empty: seats - filled,
    roster,
    unseated: unseatedStudents(students, plan).length,
    // A room with fewer seats than students is the one problem worth saying out loud up front.
    short: Math.max(0, roster - seats),
  };
};

/** Plans read newest first, which is the one somebody is most likely to want again. */
export const byUpdatedDesc = (a, b) => String(b?.updatedOn || '').localeCompare(String(a?.updatedOn || ''));
